import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import ms from 'ms'

import { MyUrlSearchParams } from '@/lib/utils'
import { useStoreCache } from '@state/store.ts'
import HttpClient from '@utils/HttpClient'
import { getNextPageParam } from '@utils/pagination/pagination'

import type { components, operations } from '@/schema'
import type { ProblemDetailsError } from '@utils/errors.ts'

export type UserDTO = components['schemas']['UserInfoWithGrants']
export type GetUserMeResponse = operations['me']['responses']['200']['content']['application/json']
export type GetUsersResponse = operations['list']['responses']['200']['content']['application/json']
type GetUsersParams = Required<operations['list']['parameters']>['query']

export type SystemRole = components['schemas']['UserSystemRole']
export type StoreRole = components['schemas']['UserStoreRole']
export type StoreGrant = components['schemas']['StoreGrantResponse']

export interface User extends UserDTO {
    fullname: string
    isAdmin: boolean
    hasAnyRoleForStore: (storeId: string, roles: StoreRole[]) => boolean
}

export const userKeys = {
  all: ['user'] as const,
  me: () => [...userKeys.all, 'me'] as const,
  search: (params: GetUsersParams) => [...userKeys.all, params.term, params.limit, params.offset] as const,
}

// TODO: might need to prevent access to store grants in favor of using useStoreGrants hook
export function useUser() {
  const storeCache = useStoreCache()
  return useQuery({
    queryKey: userKeys.me(),
    queryFn: async () => {
      const response = await HttpClient.get<GetUserMeResponse>('/api/user/me')
      response.storeGrants
        .map(grant => grant.store)
        .forEach(storeCache.seedStoreDTO)
      return mapToUser(response)
    },
    staleTime: ms('5min'),
    refetchInterval: ms('10min'),
  })
}

export function useCurrentUser() {
  const { data } = useUser()

  if (!data) {
    throw new Error('No user found. This hook should only be called within a UserGuard to ensure that the data is available.')
  }

  return data
}

export function useUsersInfinite(term?: string, limit = 25) {
  return useInfiniteQuery({
    queryKey: userKeys.search({ term, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new MyUrlSearchParams({ term, offset: pageParam, limit: limit })
      const { items, ...rest } = await HttpClient.get<GetUsersResponse>(`/api/users${searchParams.toString()}`)
      return { items: items.map(mapToUser), ...rest }
    },
    getNextPageParam: ({ pagination }) => getNextPageParam(pagination),
    initialPageParam: 0,
    staleTime: ms('5min'),
  })
}

type AddStoreGrantPathParameter = operations['grant']['parameters']['path']
export type AddStoreGrantRequestBody = operations['grant']['requestBody']['content']['application/json']
export type AddStoreGrantResponse = operations['grant']['responses']['201']['content']['application/json']

export function useAddStoreGrant() {
  const queryClient = useQueryClient()
  return useMutation<AddStoreGrantResponse, Error, AddStoreGrantPathParameter & AddStoreGrantRequestBody>({
    mutationFn: async ({ userId, storeId, role }) => {
      return await HttpClient.post(`/api/users/${userId}/grants`, { storeId, role })
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

export type DeleteStoreGrantPathParams = operations['revoke']['parameters']['path']

export function useDeleteStoreGrant() {
  const queryClient = useQueryClient()
  return useMutation<unknown, ProblemDetailsError, DeleteStoreGrantPathParams>({
    mutationFn: async ({ userId, storeId }) => {
      await HttpClient.delete(`/api/users/${userId}/grants/${storeId}`)
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: userKeys.all })
    },
  })
}

function mapToUser(dto: UserDTO): User {
  const isAdmin = dto.role === 'ADMIN'
  return {
    ...dto,
    isAdmin,
    fullname: dto.fullname ?? 'Unknown User',
    hasAnyRoleForStore: (storeId: string, roles: StoreRole[]) => {
      if (isAdmin) return true
      return dto.storeGrants.some(grant => grant.store.id === storeId && roles.includes(grant.role))
    },
  }
}